import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowUp } from "@fortawesome/free-solid-svg-icons";

import { faUserGroup } from "@fortawesome/free-solid-svg-icons";
import useNewCommonAPI from "../../api/commonAPI";
import useNewS2SAPI from "../../api/s2sAPI";

import S2SAside from "../../Components/S2SAside";
import FileUpload from "../../Components/Common/FileUpload";
import ExpiryDaysInput, {
  validateExpiryDays,
  withExpiryDaysPayload,
} from "../../Components/Common/ExpiryDaysInput";
import CurrencyRadioGroup from "../../Components/Common/CurrencyRadioGroup";
import AppButton from "../../Components/Common/Button";

import { CURRENCY_IDS } from "../../constants/currency";
import { SESSION_STORAGE_KEYS } from "../../constants/sessionStorage";

// import "../../New/Styles/pages/suyool-to-suyool.scss";
// import "../../New/Styles/s2s-aside.scss";
// import "../../New/Styles/components/form-controls.scss";
import "../../Styles/pages/bulktransfer.scss"
import { ACTION_TYPES } from "../../../app/actionTypes";
import { setActionPayload, setActionType} from "../../../features/authorizationSlice";


function readFileAsBase64(file, onLoad) {
  const reader = new FileReader();

  reader.readAsDataURL(file);

  reader.onloadend = () => {
    onLoad(reader.result);
  };
}

function triggerFileDownload(href) {
  if (!href) return;

  const link = document.createElement("a");

  link.href = href;
  link.target = "_blank";
  link.rel = "noreferrer";
  link.download = "";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ComposerCard({ icon, subtitle, title, children }) {
  return (
    <section className="new-s2s-form__card">
      {(title || subtitle || icon) && (
        <div className="new-s2s-form__card-header">
          {icon ? (
            <div className="new-s2s-form__card-icon">
              <FontAwesomeIcon icon={icon} />
            </div>
          ) : null}

          <div>
            {title ? <h2>{title}</h2> : null}
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
        </div>
      )}

      <div className="new-s2s-form__card-body">
        {children}
      </div>
    </section>
  );
}

function ComposerSplit({ children, aside }) {
  return (
    <div className="bulk__split">
      <div className="bulk__main">{children}</div>

      {aside ? (
        <aside className="bulk__aside">{aside}</aside>
      ) : null}
    </div>
  );
}

// function FormField({
//   label,
//   hint,
//   error,
//   required = false,
//   trailing,
//   children,
// }) {
//   return (
//     <div className="new-s2s-form__field">
//       <div className="new-s2s-form__field-header">
//         <label>
//           {label}
//           {required ? " *" : ""}
//         </label>

//         {trailing ? (
//           <div className="new-s2s-form__field-trailing">
//             {trailing}
//           </div>
//         ) : null}
//       </div>

//       {children}

//       {hint ? (
//         <span className="new-s2s-form__field-hint">{hint}</span>
//       ) : null}

//       {error ? (
//         <span className="new-s2s-form__field-error">{error}</span>
//       ) : null}
//     </div>
//   );
// }

function FileUploadField({
  label,
  hint,
  accept,
  selectedFile,
  error,
  downloadSampleLabel,
  onDownloadSample,
  onSelect,
  onClearError,
}) {
  return (
    <FileUpload
      label={label}
      description={
        hint || "PDF, image, or spreadsheet files up to 2MB"
      }
      accept={accept}
      maxFileSize={2 * 1024 * 1024}
      selectedFile={selectedFile}
      error={error}
      downloadSampleLabel={downloadSampleLabel}
      onDownloadSample={onDownloadSample}
      onValidationError={onClearError}
      onFileChange={onSelect}
    />
  );
}

function useS2STemplateHref() {
  const { GetS2STransferConfig } = useNewS2SAPI();

  const getS2STransferConfigRef = useRef(
    GetS2STransferConfig
  );

  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  const [templateHref, setTemplateHref] = useState(
    () =>
      sessionStorage.getItem(
        SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE
      ) || ""
  );

  const [templateLoading, setTemplateLoading] = useState(false);

  getS2STransferConfigRef.current = GetS2STransferConfig;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTemplateHref = useCallback(async () => {
    if (isFetchingRef.current) {
      return (
        sessionStorage.getItem(
          SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE
        ) || ""
      );
    }

    isFetchingRef.current = true;

    if (isMountedRef.current) {
      setTemplateLoading(true);
    }

    try {
      const response =
        await getS2STransferConfigRef.current();

      const nextTemplateHref =
        response?.Payload?.TemplateURL ||
        sessionStorage.getItem(
          SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE
        ) ||
        "";

      if (isMountedRef.current) {
        setTemplateHref(nextTemplateHref);
      }

      return nextTemplateHref;
    } catch {
      const fallbackHref =
        sessionStorage.getItem(
          SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE
        ) || "";

      if (isMountedRef.current) {
        setTemplateHref(fallbackHref);
      }

      return fallbackHref;
    } finally {
      isFetchingRef.current = false;

      if (isMountedRef.current) {
        setTemplateLoading(false);
      }
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    if (templateHref) {
      triggerFileDownload(templateHref);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    const href = await fetchTemplateHref();

    triggerFileDownload(href);
  }, [fetchTemplateHref, templateHref]);

  useEffect(() => {
    void fetchTemplateHref();
  }, [fetchTemplateHref]);

  return {
    templateHref,
    templateLoading,
    downloadTemplate,
  };
}

export function useBulkTransferForm({
  onOpenQrAfterGenerate,
} = {}) {
  const dispatch = useDispatch();

  const { getAllCurrencies } = useNewCommonAPI();

  const {
    templateHref,
    downloadTemplate,
  } = useS2STemplateHref();

  const getAllCurrenciesRef = useRef(getAllCurrencies);

  const [selectedFile, setSelectedFile] = useState(null);

  const [payload, setPayload] = useState({
    CurrencyID: CURRENCY_IDS.USD,
    ReasonFileName: "",
    formFile: null,
    DaysToExpire: "",
  });

  const [errors, setErrors] = useState({});

  getAllCurrenciesRef.current = getAllCurrencies;

  useEffect(() => {
    getAllCurrenciesRef.current();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    readFileAsBase64(
      selectedFile,
      (base64String) => {
        setPayload((currentPayload) => ({
          ...currentPayload,
          ReasonFileName: selectedFile.name,
          formFile: base64String,
        }));
      }
    );
  }, [selectedFile]);

  const updateCurrency = (value) => {
    setPayload((currentPayload) => ({
      ...currentPayload,
      CurrencyID: Number(value),
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      CurrencyID: "",
    }));
  };

  const clearFileError = () => {
    setErrors((currentErrors) => ({
      ...currentErrors,
      formFile: "",
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!payload.CurrencyID) {
      newErrors.CurrencyID = "Account is required.";
      isValid = false;
    }

    if (
      !selectedFile ||
      !selectedFile.name ||
      !selectedFile.size
    ) {
      newErrors.formFile = "File upload is required.";
      isValid = false;
    } else if (
      !/\.xlsx?$/.test(
        selectedFile.name.toLowerCase()
      )
    ) {
      newErrors.formFile =
        "Only Excel files are allowed.";
      isValid = false;
    }

    const fileSizeLimit = 2 * 1024 * 1024;

    if (
      selectedFile &&
      selectedFile.size > fileSizeLimit
    ) {
      newErrors.formFile =
        "File should not be larger than 2MB.";
      isValid = false;
    }

    const expiryDaysError = validateExpiryDays(payload.DaysToExpire, {
      required: true,
    });

    if (expiryDaysError) {
      newErrors.DaysToExpire = expiryDaysError;
      isValid = false;
    }

    setErrors(newErrors);

    return isValid;
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();

    if (!validateForm()) {
      return false;
    }

    const payloadWithExpiry =
      withExpiryDaysPayload(payload);

    dispatch(
      setActionType(
        ACTION_TYPES.PUSH_BULK_S2S_TRANSFER
      )
    );

    dispatch(
      setActionPayload(payloadWithExpiry)
    );

    onOpenQrAfterGenerate?.();

    return true;
  };

  return {
    payload,
    selectedFile,
    setSelectedFile,
    errors,
    templateHref,
    downloadTemplate,
    updateCurrency,
    clearFileError,
    setPayload,
    setErrors,
    handleSubmit,
  };
}

export function BulkTransferComposer({
  onOpenQrAfterGenerate,
}) {
  const {
    payload,
    selectedFile,
    setSelectedFile,
    errors,
    downloadTemplate,
    updateCurrency,
    clearFileError,
    setPayload,
    setErrors,
    handleSubmit,
  } = useBulkTransferForm({
    onOpenQrAfterGenerate,
  });

  return (
    <div className="new-s2s-composer">
        <div class="bulk__header">
            <div class="bulk__header__icon">
     <span className="new-s2s-form__card-icon">
    <FontAwesomeIcon icon={faUserGroup} />
  </span>
  </div>
  <div class="bulk__header__text">
    <h2>Bulk Suyool Transfers</h2>
    <p>Upload one file to transfer money to two or more recipients</p>
  </div>

</div>

    <ComposerSplit
      aside={<S2SAside />}
    >
      <ComposerCard
      >
        <form
          className="new-s2s-form"
          onSubmit={handleSubmit}
        >
          <CurrencyRadioGroup
            label="From Account"
            name="bulkCurrency"
            value={String(payload.CurrencyID)}
            onChange={updateCurrency}
            options={[
              {
                value: String(CURRENCY_IDS.USD),
                label: "USD",
              },
              {
                value: String(CURRENCY_IDS.LBP),
                label: "LBP",
              },
            ]}
          />

          {errors.CurrencyID ? (
            <span className="new-s2s-form__field-error">
              {errors.CurrencyID}
            </span>
          ) : null}

          <ExpiryDaysInput
            required
            value={payload.DaysToExpire}
            error={errors.DaysToExpire}
            onChange={(nextValue) =>
              setPayload((currentPayload) => ({
                ...currentPayload,
                DaysToExpire: nextValue,
              }))
            }
            onClearError={() =>
              setErrors((currentErrors) => ({
                ...currentErrors,
                DaysToExpire: "",
              }))
            }
          />

          <FileUploadField
            label="Upload File"
            accept=".xls,.xlsx"
            selectedFile={
              selectedFile?.size > 0
                ? selectedFile
                : null
            }
            error={errors.formFile}
            hint="Only Excel files are accepted."
            onDownloadSample={downloadTemplate}
            downloadSampleLabel="Download sample excel file"
            onClearError={clearFileError}
            onSelect={setSelectedFile}
          />

          <div className="bulk__upload__section">
            <div class="bulk__upload__icon">
            <span
              className="new-s2s-form__banner-icon"
              aria-hidden="true"
            >
              <FontAwesomeIcon
                icon={faCircleArrowUp}
              />
            </span>
            </div>

            <div class="bulk__upload__text">
              <strong>
                Bulk upload ready
              </strong>

              <p>
                Once validated, your file
                will follow the same
                authorization and
                submission flow as the
                legacy bulk transfer
                screen.
              </p>
            </div>
          </div>

          <AppButton
            type="submit"
            className="bulk__upload__continue"
          >
            Continue
          </AppButton>
        </form>
      </ComposerCard>
    </ComposerSplit>
    </div>
  );
}


export default BulkTransferComposer;
